import {
  completeBuildReportSchema,
  exhibitionLocalizationSchema,
  exhibitionManifestSchema,
  exhibitionRegistrySchema,
  museumContentSchema,
  type CompleteBuildReport,
  type ExhibitionLocalization,
  type ExhibitionManifest,
  type ExhibitionRegistry,
  type MuseumContent,
  type PublicExhibition,
} from "./contracts";
import type { Language } from "./i18n";

export type LoadPhase = "manifest" | "report" | "content" | "scene" | "validated";

export type LoadedAssets = {
  manifest: ExhibitionManifest;
  report: CompleteBuildReport;
  content: MuseumContent;
  localization: ExhibitionLocalization | null;
  glb: ArrayBuffer;
  assetBaseUrl: string;
};

export async function loadExhibitionRegistry(
  baseUrl = import.meta.env.BASE_URL,
): Promise<ExhibitionRegistry> {
  const generatedUrl = sameOriginUrl("generated/exhibitions/", baseUrl);
  return exhibitionRegistrySchema.parse(await fetchJson(new URL("index.json", generatedUrl)));
}

export async function loadMuseumAssets(
  exhibition: PublicExhibition,
  onPhase: (phase: LoadPhase) => void,
  language: Language = "en",
  baseUrl = import.meta.env.BASE_URL,
): Promise<LoadedAssets> {
  const generatedUrl = sameOriginUrl("generated/exhibitions/", baseUrl);
  const bundleUrl = new URL(`${exhibition.slug}/`, generatedUrl);
  onPhase("manifest");
  const manifestBytes = await fetchBytes(new URL(exhibition.manifest.path, generatedUrl));
  await assertHash(manifestBytes, exhibition.manifest.sha256, exhibition.manifest.path);
  assertSize(manifestBytes, exhibition.manifest.size, exhibition.manifest.path);
  const manifest = exhibitionManifestSchema.parse(
    parseJson(manifestBytes, exhibition.manifest.path),
  );
  if (
    manifest.exhibition_slug !== exhibition.slug ||
    manifest.room_ids.length !== exhibition.room_count ||
    manifest.artwork_ids.length !== exhibition.artwork_count
  ) {
    throw new Error("ASSET_IDENTITY_MISMATCH: el registro y el manifiesto no coinciden");
  }

  onPhase("report");
  const reportBytes = await fetchBytes(new URL(manifest.report.path, bundleUrl));
  await assertHash(reportBytes, manifest.report.sha256, manifest.report.path);
  assertSize(reportBytes, manifest.report.size, manifest.report.path);
  const report = completeBuildReportSchema.parse(parseJson(reportBytes, manifest.report.path));

  onPhase("content");
  const contentBytes = await fetchBytes(new URL(manifest.content.path, bundleUrl));
  await assertHash(contentBytes, manifest.content.sha256, manifest.content.path);
  assertSize(contentBytes, manifest.content.size, manifest.content.path);
  const content = museumContentSchema.parse(parseJson(contentBytes, manifest.content.path));
  const localization = await loadLocalization(manifest, bundleUrl, language, content);

  onPhase("scene");
  const glb = await fetchBytes(new URL(manifest.scene.path, bundleUrl));
  await assertHash(glb, manifest.scene.sha256, manifest.scene.path);
  assertSize(glb, manifest.scene.size, manifest.scene.path);
  validateCrossContract(manifest, report, content);
  onPhase("validated");
  return { manifest, report, content, localization, glb, assetBaseUrl: bundleUrl.href };
}

export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, "0")).join("");
}

export function validateCrossContract(
  manifest: ExhibitionManifest,
  report: CompleteBuildReport,
  content: MuseumContent,
): void {
  const expected = JSON.stringify(manifest.artwork_ids);
  if (
    manifest.exhibition_slug !== report.exhibition_slug ||
    manifest.exhibition_slug !== content.exhibition_slug ||
    JSON.stringify(report.artwork_ids) !== expected ||
    JSON.stringify(content.route.map((stop) => stop.artwork_id)) !== expected ||
    JSON.stringify(content.artworks.map((artwork) => artwork.id)) !== expected ||
    JSON.stringify(content.rooms.map((room) => room.id)) !== JSON.stringify(manifest.room_ids)
  ) {
    throw new Error("ASSET_IDENTITY_MISMATCH: la exposición publicada no coincide");
  }
  if (manifest.semantic_scene_sha256 !== report.semantic_scene_sha256) {
    throw new Error("ASSET_IDENTITY_MISMATCH: la escena semántica no coincide");
  }
  const scene = report.artifacts.find((artifact) => artifact.path === manifest.scene.path);
  const contentArtifact = report.artifacts.find(
    (artifact) => artifact.path === manifest.content.path,
  );
  if (
    scene?.sha256 !== manifest.scene.sha256 ||
    scene.size !== manifest.scene.size ||
    contentArtifact?.sha256 !== manifest.content.sha256 ||
    contentArtifact.size !== manifest.content.size ||
    report.source_hashes.content !== manifest.content.sha256
  ) {
    throw new Error("ASSET_IDENTITY_MISMATCH: el informe y los archivos no coinciden");
  }
}

async function loadLocalization(
  manifest: ExhibitionManifest,
  bundleUrl: URL,
  language: Language,
  content: MuseumContent,
): Promise<ExhibitionLocalization | null> {
  if (language === "es") return null;
  const descriptor = manifest.localizations[language];
  if (descriptor === undefined) return null;
  const bytes = await fetchBytes(new URL(descriptor.path, bundleUrl));
  await assertHash(bytes, descriptor.sha256, descriptor.path);
  assertSize(bytes, descriptor.size, descriptor.path);
  const localization = exhibitionLocalizationSchema.parse(parseJson(bytes, descriptor.path));
  if (
    localization.exhibition_slug !== content.exhibition_slug ||
    localization.language !== language ||
    JSON.stringify(localization.rooms.map((room) => room.id)) !==
      JSON.stringify(content.rooms.map((room) => room.id)) ||
    JSON.stringify(localization.artworks.map((artwork) => artwork.id)) !==
      JSON.stringify(content.artworks.map((artwork) => artwork.id))
  ) {
    throw new Error("ASSET_IDENTITY_MISMATCH: la traducción no coincide");
  }
  return localization;
}

async function fetchJson(url: URL): Promise<unknown> {
  return parseJson(await fetchBytes(url), url.pathname);
}

async function fetchBytes(url: URL): Promise<ArrayBuffer> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`ASSET_HTTP_ERROR: ${url.pathname} (${response.status})`);
  return response.arrayBuffer();
}

function parseJson(bytes: ArrayBuffer, label: string): unknown {
  try {
    return JSON.parse(new TextDecoder().decode(bytes)) as unknown;
  } catch {
    throw new Error(`ASSET_JSON_INVALID: ${label}`);
  }
}

async function assertHash(bytes: ArrayBuffer, expected: string, label: string): Promise<void> {
  if ((await sha256Hex(bytes)) !== expected) throw new Error(`ASSET_HASH_MISMATCH: ${label}`);
}

function assertSize(bytes: ArrayBuffer, expected: number, label: string): void {
  if (bytes.byteLength !== expected) throw new Error(`ASSET_SIZE_MISMATCH: ${label}`);
}

function sameOriginUrl(path: string, baseUrl: string): URL {
  const base = new URL(baseUrl, window.location.href);
  const url = new URL(path, base);
  if (url.origin !== window.location.origin) {
    throw new Error("ASSET_ORIGIN_UNSAFE: los recursos deben pertenecer al mismo origen");
  }
  return url;
}
